import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const TESTIMONIALS = [
  {
    quote: "Working with Visyra was an absolute pleasure. The team is professional, easygoing, and knows exactly how to bring out the best light and details on camera. From soft, glowing bridal looks to dramatic glam, every frame was handled with precision. Looking forward to many more collaborations.",
    name: 'Vidhya Makeovers',
    role: 'Makeup Artist',
  },
  {
    quote: "Visyra exceeded our expectations. The team was punctual, professional, and highly creative, capturing every special moment with care and detail. If you’re looking for photography that genuinely tells a story, this is the team to trust.",
    name: 'Roshith K R',
    role: 'Wedding Client',
  },
  {
    quote: "Absolutely Phenomenal Experience Working With Visyra! From The Moment We Booked Them For Our Engagement Photoshoot, The Communication Was Seamless And Stress-Free. The Team Is Incredibly Talented, Professional, And Made Us Feel So Comfortable In Front Of The Camera. The Photos Came Out To Be Really Beautiful And Made Our Day Special.",
    name: 'Lokesh B T',
    role: 'Engagement Client',
  },
  {
    quote: "We hired Visyra for our corporate event and the results were outstanding. The team was discreet yet thorough, capturing every keynote speaker, panel discussion, and networking moment. The turnaround was incredibly fast and the quality was magazine-worthy.",
    name: 'Rahul Mehta',
    role: 'Corporate Client',
  },
  {
    quote: "The fashion shoot with Visyra was a dream. They understood our brand aesthetic from the first meeting and delivered imagery that elevated our entire campaign. Creative, detail-oriented, and an absolute joy to collaborate with.",
    name: 'Ananya',
    role: 'Fashion Brand',
  },
  {
    quote: "From start to finish, the Visyra team made our destination wedding feel like a cinematic masterpiece. Every candid moment, every ritual, every emotion was captured with such artistry. The wedding film still makes us cry happy tears!",
    name: 'Priya & Arjun',
    role: 'Destination Wedding',
  },
];

/* Show 3 cards at a time on desktop, paginated */
const CARDS_PER_PAGE = 3;

export default function Testimonials() {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(TESTIMONIALS.length / CARDS_PER_PAGE);
  const startIdx = page * CARDS_PER_PAGE;
  const visible = TESTIMONIALS.slice(startIdx, startIdx + CARDS_PER_PAGE);

  const next = () => setPage((p) => (p + 1) % totalPages);
  const prev = () => setPage((p) => (p - 1 + totalPages) % totalPages);

  return (
    <section data-testid="testimonials-section" className="py-24 sm:py-32 bg-visyra-paper">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-4">What Our Clients Say</p>
          <h2
            data-testid="testimonials-heading"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-visyra-text mb-5"
          >
            Testimonials
          </h2>
          <div className="section-divider mx-auto" />
        </motion.div>

        {/* Cards */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={page}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {visible.map((t) => (
                <div
                  key={t.name}
                  data-testid={`testimonial-${t.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex flex-col bg-visyra-card border border-white/10 p-8 sm:p-10 hover:border-gold/20 transition-colors duration-500"
                >
                  {/* Quote mark */}
                  <Quote size={32} className="text-visyra-dim mb-6" strokeWidth={1.5} />

                  {/* Quote text */}
                  <p className="font-heading italic text-sm sm:text-base text-visyra-muted leading-relaxed flex-1 mb-8">
                    {t.quote}
                  </p>

                  {/* Author */}
                  <div className="border-t border-white/5 pt-5">
                    <p className="font-body text-sm font-medium text-visyra-text tracking-wide">
                      — {t.name}
                    </p>
                    <p className="font-body text-xs text-visyra-dim uppercase tracking-wider mt-1">
                      {t.role}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-6 mt-10">
            <button
              onClick={prev}
              data-testid="testimonials-prev"
              className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-gold/40 hover:text-gold text-visyra-muted transition-all duration-300"
              aria-label="Previous testimonials"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  data-testid={`testimonials-dot-${i}`}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    i === page ? 'bg-gold w-6' : 'bg-visyra-dim hover:bg-visyra-muted'
                  }`}
                  aria-label={`Page ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              data-testid="testimonials-next"
              className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-gold/40 hover:text-gold text-visyra-muted transition-all duration-300"
              aria-label="Next testimonials"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
